/**
 * CurrentTaskWidget — Module C du dashboard
 * Tâche précédente (mini), tâche en cours (hero card), tâche suivante (mini)
 */
import React, { memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Check, ChevronRight, Clock, ListTodo, Zap } from 'lucide-react';
import { haptic } from '../../shared/utils/haptics';

// ─── Palette par catégorie ──────────────────────────────────────────────────
const CAT = {
  vital:    { label: 'Vital',      emoji: '🔴', text: '#f87171', dot: '#ef4444', bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.22)',  glow: 'rgba(239,68,68,0.12)',  grad: ['#ef4444','#f87171'] },
  chore:    { label: 'Corvée',     emoji: '🟠', text: '#fbbf24', dot: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.22)', glow: 'rgba(245,158,11,0.12)', grad: ['#f59e0b','#fbbf24'] },
  pleasure: { label: 'Plaisir',   emoji: '🟢', text: '#34d399', dot: '#10b981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.22)', glow: 'rgba(16,185,129,0.12)', grad: ['#10b981','#34d399'] },
  reward:   { label: 'Récompense',emoji: '🟣', text: '#a78bfa', dot: '#8b5cf6', bg: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.22)', glow: 'rgba(139,92,246,0.12)', grad: ['#8b5cf6','#a78bfa'] },
  work:     { label: 'Travail',    emoji: '🔵', text: '#60a5fa', dot: '#3b82f6', bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.22)', glow: 'rgba(59,130,246,0.12)', grad: ['#3b82f6','#60a5fa'] },
  health:   { label: 'Santé',     emoji: '💚', text: '#2dd4bf', dot: '#14b8a6', bg: 'rgba(20,184,166,0.10)', border: 'rgba(20,184,166,0.22)', glow: 'rgba(20,184,166,0.12)', grad: ['#14b8a6','#2dd4bf'] },
};
const CAT_DEFAULT = { label: 'Tâche', emoji: '⚪', text: 'rgba(255,255,255,0.45)', dot: '#71717a', bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.08)', glow: 'rgba(0,0,0,0)', grad: ['#71717a','#a1a1aa'] };

function getCat(c) { return CAT[c] || CAT_DEFAULT; }

function fmt(t) { return t || '—'; }
function endTime(start, dur) {
  if (!start || !dur) return null;
  const [h, m] = start.split(':').map(Number);
  const tot = h * 60 + m + dur;
  return `${String(Math.floor(tot/60)).padStart(2,'0')}:${String(tot%60).padStart(2,'0')}`;
}

// ─── Barre de progression ───────────────────────────────────────────────────
const ProgressBar = memo(function ProgressBar({ progress, grad, glow }) {
  return (
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
      <motion.div
        className="h-full rounded-full"
        style={{ background: `linear-gradient(90deg, ${grad[0]}, ${grad[1]})`, boxShadow: `0 0 10px ${glow}` }}
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(progress, 100)}%` }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      />
    </div>
  );
});

// ─── Mini ligne tâche prec/suivante ────────────────────────────────────────
const MiniTaskRow = memo(function MiniTaskRow({ task, label, isCompleted, onTap, side = 'prev' }) {
  if (!task) return <div className="h-9" />;
  const cat = getCat(task.category);
  return (
    <motion.button
      onClick={() => { haptic.tap(); onTap?.(task); }}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors"
      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)' }}
      whileHover={{ background: 'rgba(255,255,255,0.04)' }}
      whileTap={{ scale: 0.98 }}
      aria-label={`${label} : ${task.task}`}
    >
      <div className="w-0.5 h-5 rounded-full flex-shrink-0 opacity-60"
        style={{ backgroundColor: isCompleted ? 'rgba(255,255,255,0.15)' : cat.dot }} />
      <span className="text-[11px] flex-1 truncate leading-tight"
        style={{ color: isCompleted ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.42)',
                 textDecoration: isCompleted ? 'line-through' : 'none' }}>
        {task.task}
      </span>
      {side === 'prev' && isCompleted && <Check className="w-3 h-3 flex-shrink-0" style={{ color: 'rgba(52,211,153,0.5)' }} />}
      {side === 'next' && (
        <span className="text-[9px] font-mono flex-shrink-0" style={{ color: 'rgba(255,255,255,0.22)' }}>
          {fmt(task.startTime || task.time)}
        </span>
      )}
    </motion.button>
  );
});

// ─── Carte tâche en cours ───────────────────────────────────────────────────
const CurrentTaskCard = memo(function CurrentTaskCard({
  task, timeRemaining, progress, subtaskCount, completedSubtasks,
  onComplete, onStartFocus, onOpenDetail,
}) {
  const cat = getCat(task.category);
  const et  = endTime(task.startTime || task.time, task.duration);

  return (
    <motion.div
      key={task.id}
      initial={{ opacity: 0, y: 14, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -14, scale: 0.97 }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
      className="w-full rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: `linear-gradient(160deg, ${cat.bg} 0%, rgba(14,14,20,0.95) 100%)`,
        border: `1px solid ${cat.border}`,
        boxShadow: `0 0 48px ${cat.glow}, 0 8px 32px rgba(0,0,0,0.4)`,
      }}
      role="region"
      aria-label={`Tâche en cours : ${task.task}`}
    >
      {/* Glow décoratif en arrière-plan */}
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${cat.glow} 0%, transparent 70%)`, opacity: 0.6 }} />

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
            style={{ color: cat.text, background: `${cat.dot}18`, border: `1px solid ${cat.dot}25` }}>
            {cat.emoji} {cat.label}
          </span>
          {task.isImported && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full"
              style={{ color: 'rgba(96,165,250,0.8)', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.20)' }}>
              📅
            </span>
          )}
        </div>
        <motion.button
          onClick={() => { haptic.medium(); onStartFocus?.(task); }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-colors"
          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
          whileTap={{ scale: 0.88 }}
        >
          <Zap className="w-3 h-3" />
          <span className="text-[10px] font-semibold">Focus</span>
        </motion.button>
      </div>

      {/* Titre */}
      <button onClick={() => { haptic.tap(); onOpenDetail?.(task); }} className="w-full text-left mb-3">
        <h2 className="text-[19px] font-bold leading-tight line-clamp-2"
          style={{ color: 'rgba(240,240,252,0.92)' }}>
          {task.task}
        </h2>
        {task.description && (
          <p className="text-xs mt-1 line-clamp-1" style={{ color: 'rgba(255,255,255,0.30)' }}>
            {task.description}
          </p>
        )}
      </button>

      {/* Temps */}
      <div className="flex items-center gap-3 mb-3" style={{ color: 'rgba(255,255,255,0.36)' }}>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          <span className="text-[11px] font-mono">{fmt(task.startTime || task.time)}{et ? ` → ${et}` : ''}</span>
        </div>
        {timeRemaining > 0 && (
          <span className="text-[11px]">
            encore <span className="font-bold font-mono" style={{ color: 'rgba(255,255,255,0.72)' }}>{timeRemaining}min</span>
          </span>
        )}
      </div>

      {/* Barre de progression */}
      <ProgressBar progress={progress} grad={cat.grad} glow={cat.glow} />

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        {subtaskCount > 0 ? (
          <div className="flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.28)' }}>
            <ListTodo className="w-3 h-3" />
            <span className="text-[10px] font-mono">{completedSubtasks}/{subtaskCount}</span>
          </div>
        ) : <div />}
        <motion.button
          onClick={() => { haptic.heavy(); onComplete?.(task); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all"
          style={{
            background: 'rgba(16,185,129,0.14)',
            border: '1px solid rgba(16,185,129,0.25)',
            color: '#34d399',
          }}
          whileTap={{ scale: 0.88 }}
          whileHover={{ background: 'rgba(16,185,129,0.22)' }}
        >
          <Check className="w-3.5 h-3.5" />
          <span className="text-[11px] font-bold">Terminé</span>
        </motion.button>
      </div>
    </motion.div>
  );
});

// ─── État vide ──────────────────────────────────────────────────────────────
const EmptyState = memo(function EmptyState({ dayStatus, nextTask, timeUntilNext, onAddTask, onOpenDetail }) {
  if (dayStatus === 'free') return (
    <div className="rounded-2xl p-5 text-center"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-3xl mb-2">🌿</p>
      <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.65)' }}>Journée libre</p>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.28)' }}>Aucune tâche planifiée</p>
      <motion.button onClick={onAddTask} whileTap={{ scale: 0.95 }}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs transition-colors"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)', color: 'rgba(255,255,255,0.45)' }}>
        + Ajouter une tâche
      </motion.button>
    </div>
  );

  if (dayStatus === 'done') return (
    <div className="rounded-2xl p-5 text-center"
      style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.16)' }}>
      <p className="text-3xl mb-2">🎉</p>
      <p className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.80)' }}>Journée terminée !</p>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.28)' }}>Bravo, tout est fait 🏆</p>
    </div>
  );

  if (nextTask) return (
    <div className="rounded-2xl p-4"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.28)' }}>😌 Rien pour l'instant</p>
      <p className="text-sm font-semibold line-clamp-1" style={{ color: 'rgba(255,255,255,0.72)' }}>
        {nextTask.task}
      </p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.28)' }}>
          {timeUntilNext != null ? `dans ${timeUntilNext}min` : fmt(nextTask.startTime)}
        </span>
        <motion.button onClick={() => { haptic.tap(); onOpenDetail?.(nextTask); }} whileTap={{ scale: 0.95 }}
          className="flex items-center gap-1 text-xs transition-colors"
          style={{ color: 'rgba(255,255,255,0.38)' }}>
          <span>Préparer</span>
          <ChevronRight className="w-3 h-3" />
        </motion.button>
      </div>
    </div>
  );

  return null;
});

// ─── Widget principal ───────────────────────────────────────────────────────
export const CurrentTaskWidget = memo(function CurrentTaskWidget({
  currentTask, previousTask, nextTask, timeRemaining, progress, dayStatus,
  timeUntilNext, isTaskCompleted, currentDayIndex, getCompletedCount,
  onComplete, onStartFocus, onOpenDetail, onAddTask,
}) {
  const subtaskCount      = currentTask?.instructions?.length || 0;
  const completedSubtasks = subtaskCount > 0
    ? (getCompletedCount?.(0, currentDayIndex, currentTask.id, subtaskCount) || 0)
    : 0;
  const prevIsCompleted = previousTask ? isTaskCompleted?.(currentDayIndex, previousTask) : false;

  return (
    <div className="flex flex-col gap-1.5 px-4" role="region" aria-label="Tâche en cours">
      <MiniTaskRow task={previousTask} label="Précédente" isCompleted={prevIsCompleted}
        onTap={onOpenDetail} side="prev" />

      <AnimatePresence mode="wait">
        {currentTask ? (
          <CurrentTaskCard
            key={currentTask.id}
            task={currentTask}
            timeRemaining={timeRemaining}
            progress={progress}
            subtaskCount={subtaskCount}
            completedSubtasks={completedSubtasks}
            onComplete={onComplete}
            onStartFocus={onStartFocus}
            onOpenDetail={onOpenDetail}
          />
        ) : (
          <EmptyState
            key="empty"
            dayStatus={dayStatus}
            nextTask={nextTask}
            timeUntilNext={timeUntilNext}
            onAddTask={onAddTask}
            onOpenDetail={onOpenDetail}
          />
        )}
      </AnimatePresence>

      <MiniTaskRow task={nextTask} label="Suivante" isCompleted={false}
        onTap={onOpenDetail} side="next" />
    </div>
  );
});

export default CurrentTaskWidget;
